import { Component, PropsWithChildren, useEffect } from "react";

import { ErrorBoundaryState } from "@components/ErrorBoundary";
import { NoDataDisclaimer } from "@components/Goals/NoDataDisclaimer";
import { PerformanceChart, PerformanceChartPlaceholder } from "@components/PerformanceChart";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body } from "@components/Typography";
import { AnalyticsEvent } from "@ctypes";
import { i18n, Translations } from "@i18n";
import { Analytics } from "@services";
import { PerformanceChartFooter } from "./PerformanceChartFooter";
import { PerformanceChartHeader } from "./PerformanceChartHeader";
import { styles } from "./styles";
import { PerformanceBreakdownProps } from "./types";

class PerformanceChartErrorBoundary extends Component<
  PropsWithChildren<PerformanceBreakdownProps>,
  ErrorBoundaryState
> {
  state = {
    error: null,
  };

  static getDerivedStateFromError(error: Error) {
    Analytics.track(AnalyticsEvent.PerformanceBreakdownChartError, { error });
    return { error };
  }

  render() {
    const { children, data } = this.props;
    const { error } = this.state;

    if (error) {
      return (
        <PerformanceChartPlaceholder data={data}>
          <Body style={styles.title}>{i18n.t(Translations.PerformanceChartTitle)}</Body>
          <Spacing size={SpacingSizes.M} />
          <NoDataDisclaimer />
        </PerformanceChartPlaceholder>
      );
    }

    return children;
  }
}

export const PerformanceBreakdown = ({ data }: PerformanceBreakdownProps) => {
  useEffect(() => {
    const { oneDay, oneWeek, oneMonth, sixMonth, oneYear, fiveYear } = data;

    Analytics.track(AnalyticsEvent.PerformanceBreakdownChartRendered, {
      oneDay: {
        nodes: oneDay.nodes.length,
        valid: oneDay.valid,
      },
      oneWeek: {
        nodes: oneWeek.nodes.length,
        valid: oneWeek.valid,
      },
      oneMonth: {
        nodes: oneMonth.nodes.length,
        valid: oneMonth.valid,
      },
      sixMonth: {
        nodes: sixMonth.nodes.length,
        valid: sixMonth.valid,
      },
      oneYear: {
        nodes: oneYear.nodes.length,
        valid: oneYear.valid,
      },
      fiveYear: {
        nodes: fiveYear.nodes.length,
        valid: fiveYear.valid,
      },
    });
  }, []);

  return (
    <PerformanceChartErrorBoundary data={data}>
      <PerformanceBreakdownComponent data={data} />
    </PerformanceChartErrorBoundary>
  );
};

export const PerformanceBreakdownComponent = ({ data }: PerformanceBreakdownProps) => {
  if (!data.oneDay.valid) {
    return (
      <PerformanceChartPlaceholder data={data}>
        <Body style={styles.title}>{i18n.t(Translations.PerformanceChartTitle)}</Body>
        <Spacing size={SpacingSizes.M} />
        <NoDataDisclaimer />
      </PerformanceChartPlaceholder>
    );
  }

  return (
    <PerformanceChart
      header={<PerformanceChartHeader />}
      footer={<PerformanceChartFooter />}
      data={data}
    />
  );
};
